import type { ReactNode } from 'react';

import MinistryIcon from '@/components/MinistryIcon';
import { IconTile } from '@/design-system/patterns/IconTile';
import { ProgressBar } from '@/design-system/ui/ProgressBar';
import { visitorsOf } from '@escala/domain/audience';
import { paletteOf } from '@escala/domain/palette';
import { attendanceSummary, presenceSummary } from '@escala/domain/presence';
import type { EventInfo } from '@escala/domain/schedule';
import type { ScheduleEvent } from '@escala/domain/types';

const n = (value: number | null) => (value === null ? '—' : value.toLocaleString('pt-BR'));

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      aria-label={title}
      className="flex flex-col gap-2.5 rounded-block border border-line-card px-3.5 py-3"
    >
      <h3 className="m-0 text-control font-bold">{title}</h3>
      {children}
    </section>
  );
}

function Stats({ items }: { items: [label: string, value: string][] }) {
  return (
    <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3">
      {items.map(([label, value]) => (
        <div key={label} className="flex flex-col">
          <dt className="text-caption text-ink-muted">{label}</dt>
          <dd data-numeric className="m-0 text-lead font-bold text-ink">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Painel do evento › Resumo: a escala, as confirmações e o check-in da equipe, o público
 * do culto e a ocupação de cada ministério, num olhar só.
 */
export default function EventSummary({ event, info }: { event: ScheduleEvent; info: EventInfo }) {
  const presence = presenceSummary(event);
  const attendance = attendanceSummary(event);
  const a = event.audience;

  return (
    <div className="flex flex-col gap-3">
      <Block title="Escala">
        <Stats
          items={[
            ['Posições preenchidas', `${info.filled} de ${info.needed}`],
            ['Pessoas escaladas', n(info.people.length)],
            ['Pendências', n(Math.max(0, info.needed - info.filled))],
            ['Conflitos', n(info.conflicts.length)],
          ]}
        />
      </Block>
      <Block title="Confirmações e check-in da equipe">
        <Stats
          items={[
            ['Confirmados', n(presence.confirmed)],
            ['Recusas', n(presence.declined)],
            ['Aguardando', n(presence.pending)],
            ['Presentes', n(attendance.present)],
            ['Faltas', n(attendance.absent)],
          ]}
        />
      </Block>
      <Block title="Público">
        {a ? (
          <Stats
            items={[
              ['Presencial', n(a.total)],
              ['Online', n(a.online)],
              ['Visitantes', n(visitorsOf(a))],
              ['Membros', n(a.members)],
              ['Crianças', n(a.kids)],
            ]}
          />
        ) : (
          <p className="m-0 text-small text-ink-subtle">Público não registrado.</p>
        )}
      </Block>
      <Block title="Ministérios">
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {info.groups.map((g) => {
            const filled = g.functions.reduce((s, f) => s + Math.min(f.people.length, f.qty), 0);
            const needed = g.functions.reduce((s, f) => s + f.qty, 0);

            return (
              <li key={g.ministry.id} className="flex items-center gap-2.5">
                <IconTile size="xs" palette={paletteOf(g.ministry.color)}>
                  <MinistryIcon icon={g.ministry.icon} />
                </IconTile>
                <span className="min-w-0 flex-1 truncate text-small font-semibold">{g.ministry.name}</span>
                <ProgressBar
                  value={needed ? Math.round((filled / needed) * 100) : 0}
                  label={`Ocupação de ${g.ministry.name}`}
                  className="w-24"
                />
                <span data-numeric className="w-12 text-right text-meta text-ink-muted">
                  {filled}/{needed}
                </span>
              </li>
            );
          })}
        </ul>
      </Block>
    </div>
  );
}
